import React from 'react';
import { CustomPicker, ColorResult, RGBColor } from 'react-color';
import {
    Saturation,
    EditableInput,
    Hue,
    Alpha
} from 'react-color/lib/components/common';
import {
    ColorPickerWrapper,
    AlphaWrapper,
    HueAlphaWrapper,
    HueWrapper,
    SaturationWrapper,
    CircleWrapper,
    EditableInputWrapper,
    ColorControls,
    Label,
    ControlersWrapper,
    CustomPointer,
    SaturationPicker,
    ColorPickerActionsContainer
} from './HBColorPicker.styles';

import ColorCircleBlock from './ColorCircleBlock';
import ColorPickerCircles from './ColorPickerCircles';
import useColorPickerConverter, {
    StringToColorResult,
    hsl2hsv,
    stringToColorResult
} from './useColorPickerConverter';
import { isOpaque, isValidColor } from '../../utils/color.utils';
import HBButton from '../HBButton/HBButton';
import HBColorPickerDrawerControls from './HBColorPickerDrawer/HBColorPickerDrawerControls';
import { useHBTranslation } from '../HBUIProvider/HBUIProvider';

type BaseProps = {
    /** Hides the alpha (opacity) slider; always true when rendered in drawer mode. */
    disableAlpha?: boolean;
    /** Array of preset color swatches shown below the picker controls. */
    presetColors?: RGBColor[] | string[];
    // eslint-disable-next-line react/no-unused-prop-types
    className?: string;
};

type HBColorPickerProps = BaseProps & {
    selectedColor?: string;
    /** Removes the transparent option from the preset color swatches. */
    removeTransparent?: boolean;
    onChange?: (selectedColor: string) => void;
    /** Heading label displayed above the preset color swatches. */
    presetColorsTitle?: string;
    /** Renders the picker inside a mobile-friendly drawer instead of the inline layout; forces alpha to be disabled. */
    showInDrawer?: boolean;
};

type ColorPickerContentProps = BaseProps & {
    selectedColor?: StringToColorResult;
    removeTransparent?: boolean;
    onChange?: (selectedColor: any) => void;
    /** Called when the Save button is clicked in actions mode; commits the staged color. */
    onSaveClicked?: () => void;
    /** Called when the Cancel button is clicked in actions mode; reverts to the previous color. */
    onCancelClicked?: () => void;
    presetColorsTitle?: string;
    /** Prevents click events on the picker from bubbling to parent elements (useful inside popovers). */
    stopPropagationOnColorPickerClick?: boolean;
    showInDrawer?: boolean;
};

const HBColorPickerContent = ({
    disableAlpha,
    presetColors,
    className,
    selectedColor,
    onChange,
    onSaveClicked,
    onCancelClicked,
    removeTransparent,
    presetColorsTitle,
    stopPropagationOnColorPickerClick,
    showInDrawer = false
}: ColorPickerContentProps) => {
    // EditableInput uses the label prop as the onChange data key
    const onColorTextChange = (data: ColorResult) => {
        const color = (data as any)['Color value'].toLowerCase();
        if (disableAlpha ? isOpaque(color) : isValidColor(color)) {
            onChange(stringToColorResult(color));
        }
    };

    const onColorSelectChange = color => {
        // if saturation is 0, it means the color is neutral (white, black, or pure gray)
        // in this case, we should allow hue changes
        // this handles the case where the user is changing the hue of a neutral color
        const isSelectedColorNeutral = selectedColor.hsv.s === 0;
        if (color.source === 'hsl' && isSelectedColorNeutral) {
            const hsv = hsl2hsv(color.h, 100, 50);
            onChange(hsv);
            return;
        }
        if (color.a === 0 || color.h === 0) {
            // check if color is hsl or hsv
            if (
                color.hasOwnProperty('h') &&
                color.hasOwnProperty('s') &&
                color.hasOwnProperty('l')
            ) {
                const hsv = hsl2hsv(color.h, 100, 50);
                onChange(hsv);
            } else {
                // eslint-disable-next-line no-param-reassign
                color.a = 1;
                onChange(color);
            }
        } else {
            onChange(color);
        }
    };

    const onColorPickerClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
        if (stopPropagationOnColorPickerClick) {
            event.stopPropagation();
        }
    };

    if (showInDrawer) {
        return (
            <HBColorPickerDrawerControls
                onColorChange={onColorSelectChange}
                onColorTextChange={onColorTextChange}
                selectedColor={selectedColor}
            />
        );
    }

    return (
        <ColorPickerWrapper className={className} onClick={onColorPickerClick}>
            <SaturationWrapper>
                <Saturation
                    color={selectedColor.hsl}
                    // @ts-ignore
                    hsl={selectedColor.hsl}
                    // @ts-ignore
                    pointer={SaturationPicker}
                    style={{
                        pointer: {
                            position: 'absolute',
                            top: `${100 - selectedColor.hsv.v}%`,
                            left: `${selectedColor.hsv.s}%`,
                            cursor: 'pointer',
                            transform: 'translate(-50%, -50%)'
                        }
                    }}
                    hsv={selectedColor.hsv}
                    onChange={onColorSelectChange}
                />
            </SaturationWrapper>

            <ControlersWrapper>
                <ColorControls>
                    <ColorCircleBlock
                        color={selectedColor?.rgb}
                        alpha={selectedColor?.rgb?.a}
                    />

                    <HueAlphaWrapper>
                        <HueWrapper>
                            <Hue
                                {...selectedColor}
                                onChange={onColorSelectChange}
                                pointer={CustomPointer}
                            />
                        </HueWrapper>

                        {!disableAlpha && (
                            <AlphaWrapper>
                                <Alpha
                                    {...selectedColor}
                                    onChange={onChange}
                                    pointer={CustomPointer}
                                />
                            </AlphaWrapper>
                        )}
                    </HueAlphaWrapper>
                </ColorControls>

                <EditableInputWrapper>
                    <EditableInput
                        onChange={onColorTextChange}
                        value={selectedColor?.hex}
                        label="Color value"
                    />
                </EditableInputWrapper>

                <CircleWrapper>
                    <Label>{presetColorsTitle}</Label>
                    <ColorPickerCircles
                        presetColors={presetColors}
                        selectedColor={selectedColor}
                        onChange={onChange}
                        removeTransparent={removeTransparent}
                    />
                </CircleWrapper>
                {!!onSaveClicked && !!onCancelClicked && (
                    <ColorPickerActionsContainer>
                        <HBButton
                            fullWidth
                            size="xs"
                            testIdName="cancel"
                            buttonTheme="secondary-solid-primary"
                            onClick={onCancelClicked}
                        >
                            Cancel
                        </HBButton>
                        <HBButton
                            fullWidth
                            size="xs"
                            testIdName="save"
                            buttonTheme="primary"
                            onClick={onSaveClicked}
                        >
                            Save
                        </HBButton>
                    </ColorPickerActionsContainer>
                )}
            </ControlersWrapper>
        </ColorPickerWrapper>
    );
};

const HBColorPickerWithHoc = CustomPicker(HBColorPickerContent);

const HBColorPicker = ({
    disableAlpha,
    presetColors,
    selectedColor,
    onChange,
    removeTransparent,
    presetColorsTitle,
    showInDrawer
}: HBColorPickerProps) => {
    const t = useHBTranslation();
    const { onChange: _onChange, color } = useColorPickerConverter(
        selectedColor,
        onChange
    );

    return (
        <HBColorPickerWithHoc
            onChange={_onChange}
            selectedColor={color}
            disableAlpha={disableAlpha}
            removeTransparent={removeTransparent}
            presetColors={presetColors}
            presetColorsTitle={
                presetColorsTitle ?? t('HBColorPicker.presetColorsTitle')
            }
            showInDrawer={showInDrawer}
        />
    );
};

export { HBColorPicker as default, HBColorPickerWithHoc };
